import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const CAR_BRANDS = [
  'Abarth', 'Alfa Romeo', 'Aston Martin', 'Audi', 'Bentley', 'BMW', 'Bugatti', 'Cadillac',
  'Chevrolet', 'Chrysler', 'Citroën', 'Dacia', 'Daewoo', 'Ferrari', 'Fiat', 'Ford',
  'Honda', 'Hyundai', 'Infiniti', 'Jaguar', 'Jeep', 'Kia', 'Lamborghini', 'Lancia',
  'Land Rover', 'Lexus', 'Maserati', 'Mazda', 'Mercedes-Benz', 'Mini', 'Mitsubishi',
  'Nissan', 'Opel', 'Peugeot', 'Porsche', 'Renault', 'Rolls-Royce', 'Seat', 'Skoda',
  'Smart', 'Subaru', 'Suzuki', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo',
  'Chery', 'Geely', 'Haval', 'MG', 'BYD', 'Aiways', 'Wuling', 'Zeekr'
];

export default function BrandSelector({ value, onChange }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = CAR_BRANDS.filter(b => b.toLowerCase().includes(search.toLowerCase()));

  const logoUrl = (brand) =>
    `https://logo.clearbit.com/${brand.toLowerCase().replace(/[^a-z0-9]/g, '').replace('mercedesbenz', 'mercedes-benz')}.com`;

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('brand_req') || (isAr ? 'الماركة *' : 'Marque *')}
      </label>
      <div
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white hover:border-blue-400 transition min-h-[42px]"
      >
        {value && (
          <img
            src={logoUrl(value)}
            alt={value}
            className="w-6 h-6 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <span className={value ? 'font-medium text-gray-900 flex-1' : 'text-gray-400 flex-1'}>
          {value || (isAr ? 'اختر الماركة...' : 'Choisir la marque...')}
        </span>
        <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 z-[999] bg-white border border-gray-200 rounded-lg shadow-xl max-h-[280px] overflow-hidden flex flex-col mt-1">
          <div className="p-2">
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={isAr ? 'ابحث عن الماركة...' : 'Rechercher...'}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {search && !CAR_BRANDS.some(b => b.toLowerCase() === search.toLowerCase()) && (
              <div
                onClick={() => { onChange(search); setOpen(false); setSearch(''); }}
                className="flex items-center gap-3 px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
              >
                <span className="text-lg">✏️</span>
                <span className="text-sm font-medium text-blue-600">"{search}"</span>
              </div>
            )}
            {filtered.map(brand => (
              <div
                key={brand}
                onClick={() => { onChange(brand); setOpen(false); setSearch(''); }}
                className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-blue-50 transition ${value === brand ? 'bg-blue-50' : ''}`}
              >
                <img
                  src={logoUrl(brand)}
                  alt={brand}
                  className="w-7 h-7 object-contain flex-shrink-0"
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text y="18" font-size="18">🚗</text></svg>'; }}
                />
                <span className="text-sm font-medium text-gray-800">{brand}</span>
                {value === brand && <span className="ms-auto text-blue-500">✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
